import styles from "./DomainEntry.module.css";
import { useState, KeyboardEvent, ChangeEvent, JSX } from "react";
import { useNavigate } from "react-router-dom";
import { useRegisterDomain } from "../../hooks/useDomain";
import logo from "../../assets/logo.png";

const DomainEntry = (): JSX.Element => {
  const [domain, setDomain] = useState<string>("");
  const [error, setError] = useState<string>("");
  const navigate = useNavigate();
  const { execute: registerDomain, isLoading } = useRegisterDomain();

  const validateDomain = (value: string): boolean => {
    const domainRegex = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    return domainRegex.test(value.trim());
  };

  const handleVerify = async (): Promise<void> => {
    const trimmed = domain.trim();
    if (!trimmed) { setError("Please enter your domain name."); return; }
    if (!validateDomain(trimmed)) { setError("Please enter a valid domain (e.g. yourdomain.com)"); return; }
    setError("");
    const result = await registerDomain(trimmed, 3000);
    if (result) {
      navigate("/verifying", { state: { domain: trimmed, domainId: result._id } });
    } else {
      setError("Failed to register domain. It may already be in use.");
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => { if (e.key === "Enter") handleVerify(); };
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => { setDomain(e.target.value); if (error) setError(""); };

  return (
    <div className={styles.page}>
      <div className={styles.blobBlue} />
      <div className={styles.blobOrange} />
      <div className={styles.container}>
        <div className={styles.logo}><img src={logo} alt="InstantGDS" className={styles.logoImg} /></div>
        <div className={styles.card}>
          <div className={styles.cardHeader}>
            <div className={styles.stepBadge}>Step 1 of 3</div>
            <h1 className={styles.title}>Connect your domain</h1>
            <p className={styles.subtitle}>Enter your custom domain to launch your travel website</p>
          </div>
          <div className={styles.inputGroup}>
            <label className={styles.label}>Your domain name</label>
            <div className={`${styles.inputWrapper} ${error ? styles.inputError : ""}`}>
              <div className={styles.inputPrefix}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="2" y1="12" x2="22" y2="12" /><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" /></svg></div>
              <input type="text" value={domain} onChange={handleChange} onKeyDown={handleKeyDown} placeholder="yourdomain.com" className={styles.input} autoFocus autoComplete="off" spellCheck={false} />
            </div>
            {error && <p className={styles.errorMsg}>{error}</p>}
          </div>
          <div className={styles.infoBox}>
            <div className={styles.infoIcon}><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" /></svg></div>
            <div className={styles.infoText}><strong>Before continuing:</strong> Point your domain&apos;s <code className={styles.code}>A record</code> to our server IP <code className={styles.code}>103.21.58.120</code></div>
          </div>
          <button onClick={handleVerify} disabled={!domain.trim() || isLoading} className={styles.btn}>
            <span>{isLoading ? "Registering..." : "Verify & Continue"}</span>
            {!isLoading && <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12" /><polyline points="12,5 19,12 12,19" /></svg>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DomainEntry;